import React, { useState, useRef, useEffect, useCallback } from 'react';
import { PaperAirplaneIcon } from '@heroicons/react/24/solid';

interface ComposeMessageProps {
  onSend: (message: string) => Promise<void>;
  disabled?: boolean;
  placeholder?: string;
}

const MAX_SMS_LENGTH = 160;
const MULTIPART_LENGTH = 153;
const MAX_HEIGHT = 120;

export const ComposeMessage: React.FC<ComposeMessageProps> = ({
  onSend,
  disabled = false,
  placeholder = 'Écrire un message...'
}) => {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const charCount = message.length;
  const smsCount = charCount === 0 ? 0 : charCount <= MAX_SMS_LENGTH ? 1 : Math.ceil(charCount / MULTIPART_LENGTH);

  // Auto-resize textarea
  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const newHeight = Math.min(textarea.scrollHeight, MAX_HEIGHT);
      textarea.style.height = `${newHeight}px`;
    }
  }, []);

  useEffect(() => {
    adjustHeight();
  }, [message, adjustHeight]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() || sending || disabled) return;

    setSending(true);
    try {
      await onSend(message.trim());
      setMessage('');
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    } catch (error) {
      // Error handling - keep message for retry
      console.error('Failed to send message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
  };

  const canSend = message.trim().length > 0 && !sending && !disabled;

  return (
    <form className="sms-compose" onSubmit={handleSubmit}>
      <div className="sms-compose-container">
        <textarea
          ref={textareaRef}
          className="sms-compose-input"
          value={message}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled || sending}
          rows={1}
        />

        <div className="sms-compose-actions">
          {charCount > 0 && (
            <div className="sms-compose-meta">
              <span className={`sms-char-count ${charCount > MAX_SMS_LENGTH ? 'sms-char-warning' : ''}`}>
                {charCount}
              </span>
              {smsCount > 1 && (
                <span className="sms-part-count">/ {smsCount} SMS</span>
              )}
            </div>
          )}

          <button
            type="submit"
            className={`sms-compose-send ${canSend ? 'sms-compose-send-active' : ''}`}
            disabled={!canSend}
            aria-label="Envoyer"
          >
            {sending ? (
              <div className="sms-compose-spinner" />
            ) : (
              <PaperAirplaneIcon className="sms-compose-send-icon" />
            )}
          </button>
        </div>
      </div>
    </form>
  );
};

export default ComposeMessage;
